<template>
    <v-form ref="form" v-model="isValid" class="reset-password-edit" @submit.prevent="submit">
        <div class="reset-password-edit__intro">
            <span class="reset-password-edit__eyebrow">PASSWORD UPDATE</span>
            <p>Use at least 8 characters and choose a password you haven’t used before.</p>
        </div>

        <div class="reset-password-edit__fields">
            <v-text-field
                v-model="oldPW"
                outlined
                dense
                :append-icon="showPassword1 ? mdiEye : mdiEyeOff"
                :type="showPassword1 ? 'text' : 'password'"
                label="Current password"
                autocomplete="current-password"
                required
                :rules="[(value) => !!value || 'Cannot be empty']"
                @click:append="showPassword1 = !showPassword1"
            />
            <v-text-field
                ref="pw1"
                v-model="newPW1"
                dense
                :append-icon="showPassword2 ? mdiEye : mdiEyeOff"
                :type="showPassword2 ? 'text' : 'password'"
                outlined
                autocomplete="new-password"
                :rules="pw1Rules"
                label="New password"
                required
                @click:append="showPassword2 = !showPassword2"
            />
            <v-text-field
                ref="pw2"
                v-model="newPW2"
                dense
                :append-icon="showPassword3 ? mdiEye : mdiEyeOff"
                :type="showPassword3 ? 'text' : 'password'"
                outlined
                autocomplete="new-password"
                :rules="pw2Rules"
                label="Confirm new password"
                required
                @click:append="showPassword3 = !showPassword3"
            />
        </div>

        <v-alert v-if="errorMessage" class="reset-password-edit__error" type="error" outlined dense role="alert">
            {{ errorMessage }}
        </v-alert>

        <footer class="reset-password-edit__actions">
            <v-btn class="reset-password-edit__cancel" :block="$vuetify.breakpoint.smAndDown" @click="cancel">
                Cancel
            </v-btn>
            <v-btn
                class="reset-password-edit__submit"
                color="primary"
                :block="$vuetify.breakpoint.smAndDown"
                :loading="submitting"
                :disabled="!formValid"
                type="submit"
            >
                Update password
            </v-btn>
        </footer>
    </v-form>
</template>

<script>
import Vue from 'vue'
import { changePassword } from '../../../auth/api.js'
import { mdiEye, mdiEyeOff } from '@mdi/js'

export default Vue.extend({
    name: `ResetPasswordEdit`,
    props: {
        userId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            mdiEye,
            mdiEyeOff,
            showPassword1: false,
            showPassword2: false,
            showPassword3: false,
            oldPW: ``,
            newPW1: ``,
            newPW2: ``,
            errorMessage: ``,
            submitting: false,
            isValid: null,
            pw1Rules: [
                (value) => value.length >= 8 || `Must be minimum 8 characters`,
                (value) => !this.newPW2 || value === this.newPW2 || `Passwords must match`
            ],
            pw2Rules: [
                (value) => (value && value.length >= 8 && value === this.newPW1) || `Passwords must match`
            ]
        }
    },
    computed: {
        formValid() {
            return this.isValid
        }
    },
    watch: {
        newPW1(value) {
            if (this.newPW2 && value.length >= 8) {
                this.$nextTick(() => this.$refs.form.validate())
            }
        },
        newPW2(value) {
            if (this.newPW1 && value.length >= 8) {
                this.$nextTick(() => this.$refs.form.validate())
            }
        }
    },
    methods: {
        async submit() {
            if (!this.formValid || this.submitting) return
            this.submitting = true
            this.errorMessage = ``
            try {
                await changePassword({
                    userId: this.userId,
                    password: this.oldPW,
                    newPassword: this.newPW1
                })
                this.$emit(`change-success`)
            } catch (error) {
                const { status } = error || {}
                switch (status) {
                    case 500:
                        this.errorMessage = `A Server error has occured`
                        break
                    case 401:
                        this.errorMessage = `Password is incorrect.`
                        break
                    default:
                        this.errorMessage = `An unknown error occured`
                }
            } finally {
                this.submitting = false
            }
        },
        cancel() {
            this.$emit(`cancel`)
        }
    }
})
</script>

<style scoped>
.reset-password-edit {
    padding: 0 18px 16px;
}
.reset-password-edit__intro {
    padding: 14px 0 4px;
}
.reset-password-edit__eyebrow {
    color: #f2a58b;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.2px;
}
.reset-password-edit__intro p {
    margin: 4px 0 0;
    color: #aeb5c3;
    font-size: 10px;
    line-height: 1.5;
}
.reset-password-edit__fields ::v-deep .v-input {
    margin-top: 10px;
}
.reset-password-edit__fields ::v-deep .v-label {
    color: #aeb5c3;
}
.reset-password-edit__fields ::v-deep input {
    color: #f1f2f5;
}
.reset-password-edit__fields ::v-deep .v-input__slot fieldset {
    border-color: rgba(255, 255, 255, 0.18);
}
.reset-password-edit__fields ::v-deep .v-input--is-focused .v-label {
    color: #ff9a74;
}
.reset-password-edit__fields ::v-deep .v-input--is-focused .v-input__slot fieldset {
    border-color: #db5120;
}
.reset-password-edit__fields ::v-deep .v-input__append-inner .v-icon {
    color: #aeb5c3;
}
.reset-password-edit__error {
    margin: 0 18px 13px;
    color: #ffd8d2;
    background: rgba(188, 54, 50, 0.1);
    border-color: rgba(242, 112, 105, 0.35);
    font-size: 11px;
}
.reset-password-edit__actions {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    padding: 3px 18px 0;
}
.reset-password-edit__actions .v-btn {
    min-height: 36px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15px;
    text-transform: none;
}
.reset-password-edit__cancel.v-btn {
    color: #c7cbd4;
    border: 1px solid rgba(255, 255, 255, 0.16);
}
.reset-password-edit__submit.v-btn {
    color: #fff;
    background: #db5120;
}
.reset-password-edit__submit.v-btn:hover {
    background: #e76638;
}
@media (max-width: 600px) {
    .reset-password-edit__actions {
        flex-direction: column-reverse;
    }
}
</style>
